import { SelectItem } from "@/models/api/SelectItem";
import CancellableHelper from "@/requests/CancellableHelper";
import {
  AddEditVindicationSetting,
  VindicationSetting,
} from "@/models/api/administration/contractors/VindicationSetting";
import {
  AutoSendMailConfigurationModel,
  EditAutoSendMailConfiguration,
  EditInvoiceSpecification,
  SpecificationColumn,
} from "@/models/api/administration/contractors/AutoSendMailConfigurationModel";
import { AddEditEmailAddress, EmailAddress } from "@/models/api/administration/contractors/EmailAddress";
import {
  BookingNotificationConfiguration,
  EditBookingNotificationConfiguration,
} from "@/models/api/administration/contractors/BookingNotificationConfiguration";

// Routes
export const URL_GET_CONTRACTOR_EMAIL_LIST = "/ContractorAdministration/${ContractorId}/EmailList";
export const URL_POST_ADD_NEW_CONTRACTOR_EMAIL = "/ContractorAdministration/${ContractorId}/EmailList/Add";

export const URL_GET_XML_GENERATORS = "/XmlGenerator/List";
export const URL_GET_AUTO_SEND_MAIL_CONFIGURATION = "/MailNotification/AutoSendMailConfiguration/${ContractorId}";
export const URL_POST_UPDATE_AUTO_SEND_MAIL_CONFIGURATION =
  "/MailNotification/AutoSendMailConfiguration/Update/${ContractorId}";
export const URL_GET_SPECIFICATION_COLUMNS = "/MailNotification/GetSpecificationColumns";
export const URL_POST_UPDATE_INVOICE_SPECIFICATION = "/MailNotification/InvoiceSpecification/Update/${ContractorId}";
export const URL_POST_DELETE_INVOICE_SPECIFICATION = "/MailNotification/InvoiceSpecification/Delete/${ContractorId}";

export const URL_GET_VINDICATION_SETTING = "/ContractorAdministration/${ContractorId}/VindicationSetting";
export const URL_POST_ADD_VINDICATION_SETTINGS = "/ContractorAdministration/${ContractorId}/VindicationSetting/Add";
export const URL_POST_EDIT_CONTRACTOR_EMAIL_ADDRESS = "/MailNotification/EmailAddress/${EmailAddressId}/Edit";
export const URL_POST_REMOVE_CONTRACTOR_EMAIL_ADDRESS = "/MailNotification/EmailAddress/${EmailAddressId}/Remove";
export const URL_POST_EDIT_VINDICATION_SETTING = "/MailNotification/Vindication/${VindicationSettingId}/Edit";

export const URL_GET_EDIT_BOOKING_NOTIFICATION = "/MailNotification/BookingNotificationConfiguration/${ContractorId}";
export const URL_POST_UPDATE_BOOKING_NOTIFICATION =
  "/MailNotification/BookingNotificationConfiguration/Update/${ContractorId}";

// Requests
const _getContractorEmailList = new CancellableHelper<Array<EmailAddress>>(URL_GET_CONTRACTOR_EMAIL_LIST, EmailAddress);
export const getContractorEmailList = async (contractorId: string): Promise<Array<EmailAddress>> => {
  return _getContractorEmailList.get({ ContractorId: contractorId });
};

const _addNewContractorEmail = new CancellableHelper<void>(URL_POST_ADD_NEW_CONTRACTOR_EMAIL, null, true);
export const addNewContractorEmail = async (contractorId: string, body: AddEditEmailAddress): Promise<void> => {
  return _addNewContractorEmail.post({ ContractorId: contractorId }, body);
};

const _getAutoSendMailConfiguration = new CancellableHelper<AutoSendMailConfigurationModel>(
  URL_GET_AUTO_SEND_MAIL_CONFIGURATION,
  AutoSendMailConfigurationModel
);
export const getAutoSendMailConfiguration = async (contractorId: string): Promise<AutoSendMailConfigurationModel> => {
  return _getAutoSendMailConfiguration.get({ ContractorId: contractorId });
};

const _updateAutoSendMailConfiguration = new CancellableHelper<void>(
  URL_POST_UPDATE_AUTO_SEND_MAIL_CONFIGURATION,
  null,
  true
);
export const updateAutoSendMailConfiguration = async (
  contractorId: string,
  model: EditAutoSendMailConfiguration
): Promise<void> => {
  return _updateAutoSendMailConfiguration.post({ ContractorId: contractorId }, model);
};

const _getSpecificationColumns = new CancellableHelper<Array<SpecificationColumn>>(
  URL_GET_SPECIFICATION_COLUMNS,
  SpecificationColumn
);
export const getSpecificationColumns = async (): Promise<Array<SpecificationColumn>> => {
  return _getSpecificationColumns.get();
};

const _updateInvoiceSpecification = new CancellableHelper<void>(URL_POST_UPDATE_INVOICE_SPECIFICATION, null, true);
export const updateInvoiceSpecification = async (
  contractorId: string,
  model: EditInvoiceSpecification
): Promise<void> => {
  return _updateInvoiceSpecification.post({ ContractorId: contractorId }, model);
};

const _deleteInvoiceSpecification = new CancellableHelper<void>(URL_POST_DELETE_INVOICE_SPECIFICATION, null, true);
export const deleteInvoiceSpecification = async (
  contractorId: string,
  documentTypeId: number | null
): Promise<void> => {
  return _deleteInvoiceSpecification.post({ ContractorId: contractorId }, documentTypeId);
};

const _getVindicationSetting = new CancellableHelper<VindicationSetting>(
  URL_GET_VINDICATION_SETTING,
  VindicationSetting,
  true
);
export const getVindicationSetting = async (contractorId: string): Promise<VindicationSetting> => {
  return _getVindicationSetting.get({ ContractorId: contractorId });
};

const _addVindicationSetting = new CancellableHelper<void>(URL_POST_ADD_VINDICATION_SETTINGS, null, true);
export const addVindicationSetting = async (contractorId: string, body: AddEditVindicationSetting): Promise<void> => {
  return _addVindicationSetting.post({ ContractorId: contractorId }, body);
};

const _editContractorEmailAddress = new CancellableHelper<void>(URL_POST_EDIT_CONTRACTOR_EMAIL_ADDRESS, null, true);
export const editContractorEmailAddress = async (emailAddressId: number, body: AddEditEmailAddress): Promise<void> => {
  return _editContractorEmailAddress.post({ EmailAddressId: emailAddressId }, body);
};

const _removeContractorEmailAddress = new CancellableHelper<void>(URL_POST_REMOVE_CONTRACTOR_EMAIL_ADDRESS, null, true);
export const removeContractorEmailAddress = async (emailAddressId: number): Promise<void> => {
  return _removeContractorEmailAddress.post({ EmailAddressId: emailAddressId });
};

const _editVindicationSetting = new CancellableHelper<void>(URL_POST_EDIT_VINDICATION_SETTING, null, true);
export const editVindicationSetting = async (
  vindicationSettingId: number,
  body: AddEditVindicationSetting
): Promise<void> => {
  return _editVindicationSetting.post({ VindicationSettingId: vindicationSettingId }, body);
};

const _getXmlGenerators = new CancellableHelper<Array<SelectItem>>(URL_GET_XML_GENERATORS, SelectItem);
export const getXmlGenerators = async (): Promise<Array<SelectItem>> => {
  return _getXmlGenerators.get();
};

const _getBookingNotification = new CancellableHelper<BookingNotificationConfiguration>(
  URL_GET_EDIT_BOOKING_NOTIFICATION,
  BookingNotificationConfiguration
);
export const getBookingNotification = async (contractorId: string): Promise<BookingNotificationConfiguration> => {
  return _getBookingNotification.get({ ContractorId: contractorId });
};

const _updateBookingNotification = new CancellableHelper<void>(URL_POST_UPDATE_BOOKING_NOTIFICATION, null, true);
export const updateBookingNotification = async (
  contractorId: string,
  model: EditBookingNotificationConfiguration
): Promise<void> => {
  return _updateBookingNotification.post({ ContractorId: contractorId }, model);
};
